/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';

type EntityItem = { key: string; name: string; totalTags: number; precision: number; recall: number; f1Score: number };

export type AutotagTriggerModalSelectEntitiesProps = TableProps<EntityItem> & {
    onDismiss: () => void;
    onPrevious: () => void;
    autotagInfoDto: AutoLabelingInfoDto;
    onSubmit: (entitiesNames: string[]) => void;
};

const messages = defineMessages({
    recall: { id: 'customText.selectEntitiesScreen.recall', defaultMessage: 'Recall' },
    f1Score: { id: 'customText.selectEntitiesScreen.f1Score', defaultMessage: 'F1 Score' },
    entities: { id: 'customText.selectEntitiesScreen.entities', defaultMessage: 'Entities' },
    precision: { id: 'customText.selectEntitiesScreen.precision', defaultMessage: 'Precision' },
    totalTags: { id: 'customText.selectEntitiesScreen.totalTags', defaultMessage: 'Total labels' },
    title: { id: 'customText.selectEntitiesScreen.title', defaultMessage: 'Select entities to be auto-labeled' },
    description: {
        id: 'customText.selectEntitiesScreen.description',
        defaultMessage:
            'The below statistics belong to the selected trained model. By default all entities are included in the auto-labeling process'
    },
    gptDescription: {
        id: 'customText.selectEntitiesScreen.gptDescription',
        defaultMessage: `Select which entities you’d like to auto-label. By default, all existing
        entities are included in the auto-labeling process. You must have at least 1 entity to auto-label.`
    }
});

const theme = getScopedTheme('CustomTextAutotagTriggerModalSelectEntitiesScreen');

const { styleComponent } = getStylistV2('AutotagTriggerModalSelectEntities');

const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });

const scrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { minHeight: '400px', height: '100%', position: 'relative' }
});

const barContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { flex: 4, width: '100%', border: '1px solid' }
});
const barStyles = (width: number) =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
        root: { height: '100%', width: `${width * 100}%`, backgroundColor: theme.entitiesTagsColor }
    });

export const SelectEntitiesScreen = observer((props: AutotagTriggerModalSelectEntitiesProps) => {
    const { autotagInfoDto, onSubmit, onDismiss, onPrevious, ...rest } = props;
    const { trainedModelLabel } = autotagInfoDto;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const entitiesTableRef = React.useRef<HTMLDivElement>();
    const modelEvaluationFetchingTracker = useProgressTracker();
    const { selection, selectedItems } = useSelection<EntityItem>();

    const isGptJob = autotagInfoDto.autoLabelingJobType === 'gpt';
    const totalTags = rootStore.currentCustomTextProjectStore.tagsCount;
    const entities = rootStore.currentCustomTextProjectStore.extractionStore.entities ?? [];
    const modelEntities =
        rootStore.currentCustomTextProjectStore.modelsEvaluationMap.get(trainedModelLabel)?.performanceMetricsBreakdown ?? [];

    const items: EntityItem[] = entities.map(entity => ({
        key: entity.name,
        name: entity.name,
        totalTags: rootStore.currentCustomTextProjectStore.labelsCount.get(entity.id) ?? 0,
        precision: modelEntities.find(ent => ent.name === entity.name)?.precision ?? 0,
        recall: modelEntities.find(ent => ent.name === entity.name)?.recall ?? 0,
        f1Score: modelEntities.find(ent => ent.name === entity.name)?.f1Score ?? 0
    }));

    React.useEffect(() => {
        if (!isGptJob) {
            dispatcher.dispatch('getTrainedModelEvaluationData', {
                trainedModelName: trainedModelLabel,
                tracker: modelEvaluationFetchingTracker,
                toastData: {}
            });
        }
        if (autotagInfoDto.entitiesNames.length > 0) {
            autotagInfoDto.entitiesNames.forEach(entity => selection.setKeySelected(entity, true, false));
        } else {
            selection.setAllSelected(true);
        }
    }, [trainedModelLabel]);

    /**
     * The column header cell is a button contains entity name and a callout which is focusable and it's not valid in a11y
     * that a button contains nested interactive elements so this useEffect gets that cell and changes its role to be menu.
     * And the callout button will be a menuitem.
     */
    React.useEffect(() => {
        const columnHeaders = entitiesTableRef?.current?.getElementsByClassName('ms-DetailsHeader-cellTitle');
        for (const header of columnHeaders) {
            document.getElementById(header.id)?.setAttribute('role', 'menu');
        }
    });

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode) => (
        <Stack role="menuitem" horizontal verticalAlign="center">
            <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }}>{label}</LabelContainer>
            <CalloutButton content={tooltipText} />
        </Stack>
    );

    const columns: IColumn[] = [
        {
            key: 'name',
            name: localize(messages.entities),
            onRenderHeader: () => labelOnRender(localize(messages.entities), localize(messages.entities)),
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            onRender: (item: EntityItem) => <LuisText>{item.name}</LuisText>
        },
        {
            key: 'totalTags',
            name: localize(messages.totalTags),
            onRenderHeader: () => labelOnRender(localize(messages.totalTags), localize(messages.totalTags)),
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: EntityItem) => (
                <Stack horizontal tokens={{ childrenGap: 5 }} grow>
                    <Stack styles={barContainerStyles}>
                        <Stack styles={barStyles(item.totalTags / totalTags)} />
                    </Stack>
                    <LuisText styles={{ root: { flex: 1 } }}>
                        {item.totalTags}/{totalTags}
                    </LuisText>
                </Stack>
            )
        },
        ...(isGptJob
            ? []
            : [
                  {
                      key: 'precision',
                      name: localize(messages.precision),
                      onRenderHeader: () => labelOnRender(localize(messages.precision), localize(messages.precision)),
                      minWidth: 50,
                      maxWidth: 100,
                      isResizable: true,
                      onRender: (item: EntityItem) => (
                          <LuisText>{numberToPercentageString({ ratio: item.precision, showPercentage: false })}</LuisText>
                      )
                  },
                  {
                      key: 'recall',
                      name: localize(messages.recall),
                      onRenderHeader: () => labelOnRender(localize(messages.recall), localize(messages.recall)),
                      minWidth: 50,
                      maxWidth: 100,
                      isResizable: true,
                      onRender: (item: EntityItem) => (
                          <LuisText>{numberToPercentageString({ ratio: item.recall, showPercentage: false })}</LuisText>
                      )
                  },
                  {
                      key: 'f1Score',
                      name: localize(messages.f1Score),
                      onRenderHeader: () => labelOnRender(localize(messages.f1Score), localize(messages.f1Score)),
                      minWidth: 50,
                      maxWidth: 100,
                      isResizable: true,
                      onRender: (item: EntityItem) => (
                          <LuisText>{numberToPercentageString({ ratio: item.f1Score, showPercentage: false })}</LuisText>
                      )
                  }
              ])
    ];

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '100px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(isGptJob ? messages.gptDescription : messages.description)}</LuisText>
                </Stack>
            </Stack>
            <Stack.Item grow tokens={{ padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <ScrollablePane styles={scrollablePaneStyles}>
                    <div ref={entitiesTableRef}>
                        <Table<EntityItem> items={items} columns={columns} selection={selection} {...rest} />
                    </div>
                </ScrollablePane>
            </Stack.Item>
            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={AutoLabelingSteps.SelectEntitiesOrClasses}
                isLoading={modelEvaluationFetchingTracker.isRunning}
                onNext={() => onSubmit(selectedItems.map(sI => sI.name))}
                isValid={
                    getAutoLabelingStepStatus(
                        AutoLabelingSteps.SelectEntitiesOrClasses,
                        AutoLabelingSteps.SelectEntitiesOrClasses,
                        { ...autotagInfoDto, entitiesNames: selectedItems.map(e => e.name) },
                        rootStore
                    ).isValid
                }
            />
        </Stack>
    );
});
